import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery as useApolloQuery } from "@apollo/client/react";
import ChargesRow from "src/components/billing/ChargesRow";
import OneTimeChargesModal from "src/components/project/inbound/OneTimeChargesModal";
import CategoryChargesInfoModal from "src/components/billing/CategoryChargesInfoModal";
import InventoryRequestCategoryChargesEditModal from "src/components/billing/InventoryRequestCategoryChargesEditModal";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import { UPDATE_INTERNAL_WORK_ORDER_MUTATION } from "src/constants/graphql/mutations";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import {
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "src/components/project/settings/utils/billingTypes";
import InternalWorkOrderChargesHeader from "./InternalWorkOrderChargesHeader";
import InternalWorkOrderChargesInfoModal from "./InternalWorkOrderChargesInfoModal";
import InternalBillingQuantitiesModal from "./internal-work-order-guided-wizard/InternalBillingQuantitiesModal";

const chargeTitles = {
  internalWorkOrderCharges: "Internal Work Order Charges",
  laborCharges: "Labor Charges",
  materialCharges: "Material Charges",
  equipmentCharges: "Equipment Charges",
};

const FLAT_RATE_BILLING_TYPE = "Flat Rate/As Quoted (Internal Work Orders)";
const NOT_APPLICABLE_BILLING_TYPE = "Not Applicable";

const isTimeAndMaterialsBillingType = billingType =>
  [TIME_AND_MATERIALS_BLENDED, TIME_AND_MATERIALS_ITEMIZED].includes(billingType);

const extractQuantities = (charges) => {
  if (!charges) return undefined;
  const quantities = {};
  Object.entries(charges).forEach(([key, value]) => {
    if (!["calculated_at", "total"].includes(key) && value?.quantity != null) {
      quantities[key] = value.quantity;
    }
  });
  return Object.keys(quantities).length > 0 ? quantities : undefined;
};

const extractFlatRateAmount = billingQuantities => {
  if (!billingQuantities) return "";
  if (Array.isArray(billingQuantities)) {
    const flatRateEntry = billingQuantities.find(
      entry => entry.quantity_type === "flat_rate" || entry.quantityType === "flat_rate"
    );
    return flatRateEntry?.quantity_count || flatRateEntry?.quantityCount || "";
  }
  return billingQuantities.flat_rate || billingQuantities.flatRate || "";
};

export default function InternalWorkOrderChargesSummaryModal({ internalWorkOrder, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const billing = internalWorkOrder?.billingCharges || {};
  const projectId = internalWorkOrder?.project?.id;
  const { data: { project } = {} } = useApolloQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const [openOneTime, setOpenOneTime] = useState(false);
  const openChargesInfoModal = queryParams.get("openChargesInfoModal");
  const savedBillingType = internalWorkOrder?.billingType || NOT_APPLICABLE_BILLING_TYPE;
  const savedFlatRateAmount = extractFlatRateAmount(internalWorkOrder?.billingQuantities);
  const [persistedBillingType, setPersistedBillingType] = useState(savedBillingType);
  const [persistedFlatRateAmount, setPersistedFlatRateAmount] = useState(savedFlatRateAmount);
  const [draftBillingType, setDraftBillingType] = useState(savedBillingType);
  const [draftFlatRateAmount, setDraftFlatRateAmount] = useState(savedFlatRateAmount);

  const toggleChargesInfoModal = useCallback(
    (value) => {
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set("openChargesInfoModal", value);
      } else {
        params.delete("openChargesInfoModal");
      }
      navigate({ search: params.toString() });
    },
    [navigate, location.search]
  );

  const [openChargesEditModal, setOpenChargesEditModal] = useState(null);
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(false);
  const [updateInternalWorkOrder, { loading }] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    setPersistedBillingType(savedBillingType);
    setPersistedFlatRateAmount(savedFlatRateAmount);
    setDraftBillingType(savedBillingType);
    setDraftFlatRateAmount(savedFlatRateAmount);
  }, [savedBillingType, savedFlatRateAmount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("returnedFromEditRates") !== "true") return;

    params.delete("returnedFromEditRates");
    navigate({ search: params.toString() }, { replace: true });

    const input = { billingType: internalWorkOrder.billingType };
    const laborCharges = extractQuantities(internalWorkOrder.laborCharges);
    const materialCharges = extractQuantities(internalWorkOrder.materialCharges);
    const equipmentCharges = extractQuantities(internalWorkOrder.equipmentCharges);
    if (laborCharges) input.laborCharges = laborCharges;
    if (materialCharges) input.materialCharges = materialCharges;
    if (equipmentCharges) input.equipmentCharges = equipmentCharges;

    updateInternalWorkOrder({
      variables: {
        id: internalWorkOrder.id,
        input,
      },
    }).catch(err => {
      console.error("Failed to recalculate IWO billing after rate edit:", err);
    });
  }, [location.search]);

  const hasBillingConfigurationChanges =
    draftBillingType !== persistedBillingType ||
    (draftBillingType === FLAT_RATE_BILLING_TYPE &&
      String(draftFlatRateAmount || "") !== String(persistedFlatRateAmount || ""));

  const showTimeAndMaterialsCharges = isTimeAndMaterialsBillingType(draftBillingType);

  const handleOpenBillingQuantities = useCallback(() => {
    if (!isTimeAndMaterialsBillingType(draftBillingType)) {
      notifyError("Select a time and materials billing type to edit billable quantities");
      return;
    }
    setOpenBillingQuantitiesModal(true);
  }, [draftBillingType, notifyError]);

  const toNumber = (value) => (typeof value === "number" && !Number.isNaN(value) ? value : 0);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const result = await updateInternalWorkOrder({
          variables: {
            id: internalWorkOrder.id,
            input: {
              oneTimeCharges: values,
            },
          },
        });

        if (result.data?.updateInternalWorkOrder?.errors?.length > 0) {
          const errorMessage = result.data.updateInternalWorkOrder.errors[0].message;
          notifyError(errorMessage);
        } else {
          notifySuccess("One-time charges saved successfully");
          setOpenOneTime(false);
        }
      } catch (err) {
        notifyError("Failed to save charges");
      }
    },
    [internalWorkOrder.id, updateInternalWorkOrder, notifyError, notifySuccess]
  );

  const handleSaveBillingConfiguration = useCallback(async () => {
    try {
      if (draftBillingType === FLAT_RATE_BILLING_TYPE && !draftFlatRateAmount) {
        notifyError("Flat rate amount is required");
        return;
      }

      let billingQuantities = internalWorkOrder?.billingQuantities;
      if (draftBillingType === FLAT_RATE_BILLING_TYPE) {
        billingQuantities = [
          {
            quantity_type: "flat_rate",
            quantity_count: parseFloat(draftFlatRateAmount),
          },
        ];
      } else if (
        draftBillingType === NOT_APPLICABLE_BILLING_TYPE ||
        draftBillingType !== savedBillingType
      ) {
        billingQuantities = [];
      }

      const result = await updateInternalWorkOrder({
        variables: {
          id: internalWorkOrder.id,
          input: {
            billingType: draftBillingType,
            billingQuantities,
          },
        },
      });

      if (result.data?.updateInternalWorkOrder?.errors?.length > 0) {
        const errorMessage = result.data.updateInternalWorkOrder.errors[0].message;
        notifyError(errorMessage);
        return;
      }

      setPersistedBillingType(draftBillingType);
      setPersistedFlatRateAmount(draftFlatRateAmount);
      notifySuccess("Billing configuration saved successfully");
    } catch (err) {
      notifyError("Failed to save billing configuration");
    }
  }, [
    draftBillingType,
    draftFlatRateAmount,
    internalWorkOrder.id,
    internalWorkOrder?.billingQuantities,
    notifyError,
    notifySuccess,
    savedBillingType,
    updateInternalWorkOrder,
  ]);

  const handleBillingTypeChange = useCallback(
    async (nextBillingType) => {
      if (nextBillingType === persistedBillingType) {
        setDraftBillingType(nextBillingType);
        return;
      }

      const nextFlatRateAmount = "";
      setDraftBillingType(nextBillingType);
      setDraftFlatRateAmount(nextFlatRateAmount);

      try {
        const result = await updateInternalWorkOrder({
          variables: {
            id: internalWorkOrder.id,
            input: {
              billingType: nextBillingType,
              billingQuantities: [],
            },
          },
        });

        if (result.data?.updateInternalWorkOrder?.errors?.length > 0) {
          const errorMessage = result.data.updateInternalWorkOrder.errors[0].message;
          setDraftBillingType(persistedBillingType);
          setDraftFlatRateAmount(persistedFlatRateAmount);
          notifyError(errorMessage);
          return;
        }

        setPersistedBillingType(nextBillingType);
        setPersistedFlatRateAmount(nextFlatRateAmount);
        notifySuccess("Billing type updated successfully");
      } catch (err) {
        setDraftBillingType(persistedBillingType);
        setDraftFlatRateAmount(persistedFlatRateAmount);
        notifyError("Failed to update billing type");
      }
    },
    [
      internalWorkOrder.id,
      notifyError,
      notifySuccess,
      persistedBillingType,
      persistedFlatRateAmount,
      updateInternalWorkOrder,
    ]
  );

  const charges = {
    internalWorkOrderCharges: toNumber(billing.internal_work_order_total ?? billing.base_total),
    laborCharges: toNumber(billing.labor_total),
    materialCharges: toNumber(billing.material_total),
    equipmentCharges: toNumber(billing.equipment_total),
    oneTimeCharges: toNumber(billing.one_time_total),
    salesTax: toNumber(internalWorkOrder?.salesTax?.total),
    billingCharges: toNumber(billing.grand_total),
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={false}
        disableEnforceFocus={!!openChargesInfoModal}
        disableAutoFocus={!!openChargesInfoModal}
        disableRestoreFocus={!!openChargesInfoModal}
        sx={{
          ...(openChargesInfoModal ? { zIndex: theme => theme.zIndex.modal - 1 } : {}),
          "& .MuiDialog-paper": { maxWidth: "440px", width: "100%" },
        }}
        aria-labelledby="iwo-charges-summary-title"
      >
        <DialogTitle id="iwo-charges-summary-title">
          <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
            Internal Work Order Charges Summary
          </Typography>
          <IconButton
            onClick={onClose}
            size="large"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <InternalWorkOrderChargesHeader internalWorkOrder={internalWorkOrder} />
          <Box display="flex" flexDirection="column" gap={1} mx="auto" mt={3}>
            <ChargesRow
              value={charges.internalWorkOrderCharges}
              label="IWO Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("internalWorkOrderCharges")}
            />
            <ChargesRow
              value={charges.laborCharges}
              label="Labor Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("laborCharges")}
            />
            <ChargesRow
              value={charges.materialCharges}
              label="Material Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("materialCharges")}
            />
            <ChargesRow
              value={charges.equipmentCharges}
              label="Equipment Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("equipmentCharges")}
            />
            <ChargesRow
              value={charges.oneTimeCharges}
              label="One-Time Flat Charges"
              operator="plus"
              onInfoClick={() => setOpenOneTime(true)}
            />
            <ChargesRow
              value={charges.salesTax}
              label="Sales Tax"
              operator="plus"
              showInfoButton={false}
            />
            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.5)" }} />
            <ChargesRow
              value={charges.billingCharges}
              label="Grand Total"
              operator="equals"
              showInfoButton={false}
            />
          </Box>
        </DialogContent>
      </Dialog>
      {openChargesInfoModal && (
        <>
          {openChargesInfoModal === "internalWorkOrderCharges" ? (
            <InternalWorkOrderChargesInfoModal
              onClose={() => toggleChargesInfoModal(null)}
              internalWorkOrder={internalWorkOrder}
              billingType={draftBillingType}
              savedBillingType={persistedBillingType}
              flatRateAmount={draftFlatRateAmount}
              onBillingTypeChange={handleBillingTypeChange}
              onFlatRateAmountChange={setDraftFlatRateAmount}
              onSaveBillingConfiguration={handleSaveBillingConfiguration}
              onEditBillableQuantities={() => {
                toggleChargesInfoModal(null);
                handleOpenBillingQuantities();
              }}
              loading={loading}
              hasBillingConfigurationChanges={hasBillingConfigurationChanges}
            />
          ) : (
            <CategoryChargesInfoModal
              onClose={() => toggleChargesInfoModal(null)}
              title={chargeTitles[openChargesInfoModal]}
              charges={internalWorkOrder?.[openChargesInfoModal]}
              categoryKey={openChargesInfoModal}
              project={project}
              entity={internalWorkOrder}
              onEdit={() => {
                setOpenChargesEditModal(openChargesInfoModal);
                toggleChargesInfoModal(null);
              }}
            />
          )}
        </>
      )}
      {openChargesEditModal && (
        <InventoryRequestCategoryChargesEditModal
          open={true}
          onClose={() => {
            toggleChargesInfoModal(openChargesEditModal);
            setOpenChargesEditModal(null);
          }}
          title={chargeTitles[openChargesEditModal]}
          charges={internalWorkOrder?.[openChargesEditModal]}
          project={project}
          inventoryRequest={internalWorkOrder}
          categoryKey={openChargesEditModal}
          entityType="internalWorkOrder"
        />
      )}
      {openOneTime && (
        <OneTimeChargesModal
          shipment={internalWorkOrder}
          onClose={() => setOpenOneTime(false)}
          onSubmit={handleSubmit}
          loading={loading}
        />
      )}
      {openBillingQuantitiesModal && showTimeAndMaterialsCharges && (
        <InternalBillingQuantitiesModal
          internalWorkOrder={{ ...internalWorkOrder, billingType: draftBillingType }}
          onSave={() => {
            setOpenBillingQuantitiesModal(false);
            toggleChargesInfoModal("internalWorkOrderCharges");
          }}
          onClose={() => setOpenBillingQuantitiesModal(false)}
        />
      )}
    </>
  );
}
